import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, Filter, PlusCircle, Clock, Users, ChefHat } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useRecipeStore } from '@/stores/recipe-store'
import { useAuthStore } from '@/stores/auth-store'
import { useHouseholdStore } from '@/stores/household-store'
import { getEquipmentIcon } from '@/lib/equipment'
import { DifficultyIndicator } from '@/components/recipe/difficulty-indicator'

export default function RecipesPage() {
  const { getFilteredRecipes, filters, setFilters, clearFilters, recipes, loading, error, loadRecipes } = useRecipeStore()
  const { user } = useAuthStore()
  const { currentHousehold, viewMode } = useHouseholdStore()
  const [showFilters, setShowFilters] = useState(false)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    // Fetch recipes when user is authenticated
    if (user) {
      void loadRecipes()
    }
  }, [loadRecipes, user])

  // Handle URL filter parameters on mount
  useEffect(() => {
    const filter = searchParams.get('filter')
    if (filter) {
      switch (filter) {
        case 'favoriet':
          // Just set a search term for high-rated recipes since we don't have favoriet tag
          setFilters({ ...filters, search: '' })
          break
        case 'snel':
          // Filter for recipes with <= 30 minutes total time
          setFilters({ ...filters, maxTime: 30 })
          break
        default:
          break
      }
    }
  }, []) // Only run on mount

  const filteredRecipes = useMemo(
    () => getFilteredRecipes(user?.id, currentHousehold?.id, viewMode),
    [currentHousehold?.id, getFilteredRecipes, user?.id, viewMode]
  )

  const handleSearchChange = useCallback((value: string) => {
    setFilters({ ...filters, search: value })
  }, [setFilters, filters])

  const handleDifficultyFilter = useCallback((difficulty: string) => {
    setFilters({ 
      ...filters, 
      difficulty: filters.difficulty === difficulty ? undefined : difficulty 
    })
  }, [setFilters, filters])

  const handleTagFilter = useCallback((tag: string) => {
    const currentTags = filters.tags || []
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag]
    setFilters({ ...filters, tags: newTags })
  }, [setFilters, filters])

  const handleEquipmentFilter = useCallback((equipment: string) => {
    const currentEquipment = filters.equipment || []
    const newEquipment = currentEquipment.includes(equipment)
      ? currentEquipment.filter(e => e !== equipment)
      : [...currentEquipment, equipment]
    setFilters({ ...filters, equipment: newEquipment })
  }, [setFilters, filters])

  const handleTimeFilter = useCallback((maxTime: number) => {
    setFilters({ 
      ...filters, 
      maxTime: filters.maxTime === maxTime ? undefined : maxTime 
    })
  }, [setFilters, filters])

  // Get unique tags and equipment from all recipes
  const allTags = Array.from(new Set(recipes.flatMap(r => r.tags || [])))
  const allEquipment = Array.from(new Set(recipes.flatMap(r => r.equipment || [])))

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0)
  )

  return (
    <div className="container py-6 sm:py-8">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-slate-50 sm:text-3xl">
            Mijn Recepten ({filteredRecipes.length})
          </h1>
          <p className="text-gray-600 dark:text-slate-300">
            Ontdek en beheer je receptencollectie
          </p>
        </div>
        
        <div className="flex flex-col gap-3 sm:flex-row md:mt-0">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className="relative w-full sm:w-auto"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-3 w-3 bg-orange-500 rounded-full" />
            )}
          </Button>
          
          <Button asChild className="w-full sm:w-auto">
            <Link to="/recepten/nieuw">
              <PlusCircle className="h-4 w-4 mr-2" />
              Nieuw Recept
            </Link>
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Zoek op naam, ingrediënten of beschrijving..."
            value={filters.search || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Filters</CardTitle>
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                >
                  Wis Alle Filters
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Difficulty Filter */}
            <div>
                <h3 className="font-medium mb-3 text-gray-900 dark:text-slate-100">Moeilijkheidsgraad</h3>
              <div className="flex flex-wrap gap-2">
                {['gemakkelijk', 'middel', 'moeilijk'].map((difficulty) => (
                  <Button
                    key={difficulty}
                    variant={filters.difficulty === difficulty ? "primary" : "outline"}
                    size="sm"
                    onClick={() => handleDifficultyFilter(difficulty)}
                    className="capitalize"
                  >
                    {difficulty}
                  </Button>
                ))}
              </div>
            </div>

            {/* Time Filter */}
            <div>
                <h3 className="font-medium mb-3 text-gray-900 dark:text-slate-100">Maximale Tijd</h3>
              <div className="flex flex-wrap gap-2">
                {[15, 30, 60, 120].map((time) => (
                  <Button
                    key={time}
                    variant={filters.maxTime === time ? "primary" : "outline"}
                    size="sm"
                    onClick={() => handleTimeFilter(time)}
                  >
                    <Clock className="h-3 w-3 mr-1" />
                    {time} min
                  </Button>
                ))}
              </div>
            </div>

            {/* Tags Filter */}
            {allTags.length > 0 && (
              <div>
                <h3 className="font-medium mb-3 text-gray-900 dark:text-slate-100">Tags</h3>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {allTags.map((tag) => (
                    <Button
                      key={tag}
                      variant={(filters.tags || []).includes(tag) ? "primary" : "outline"}
                      size="sm"
                      onClick={() => handleTagFilter(tag)}
                    >
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment Filter */}
            {allEquipment.length > 0 && (
              <div>
                <h3 className="font-medium mb-3 text-gray-900 dark:text-slate-100">Benodigde Apparatuur</h3>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {allEquipment.map((equipment) => (
                    <Button
                      key={equipment}
                      variant={(filters.equipment || []).includes(equipment) ? "primary" : "outline"}
                      size="sm"
                      onClick={() => handleEquipmentFilter(equipment)}
                    >
                      {getEquipmentIcon(equipment)} {equipment}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Authentication Check */}
      {!user ? (
        <div className="py-12 text-center">
          <div className="mx-auto max-w-md rounded-lg border border-blue-200 bg-blue-50 p-6 dark:border-blue-800 dark:bg-slate-900">
            <h3 className="text-lg font-medium text-blue-800 dark:text-blue-100 mb-2">Inloggen vereist</h3>
            <p className="text-blue-600 dark:text-blue-100 mb-4">Je moet ingelogd zijn om je recepten te bekijken.</p>
            <Button asChild>
              <Link to="/login">Inloggen</Link>
            </Button>
          </div>
        </div>
      ) : loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-300">Recepten laden...</p>
        </div>
      ) : error ? (
        <div className="py-12 text-center">
          <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-900 dark:bg-slate-900">
            <h3 className="text-lg font-medium text-red-800 dark:text-red-100 mb-2">Fout bij laden</h3>
            <p className="text-red-600 dark:text-red-100 mb-4">{error}</p>
            <Button 
              variant="outline" 
              onClick={() => void loadRecipes()}
            >
              Opnieuw proberen
            </Button>
          </div>
        </div>
      ) : recipes.length === 0 ? (
        <div className="text-center py-12">
          <ChefHat className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">Nog geen recepten</h3>
          <p className="text-gray-600 dark:text-slate-300 mb-6">Begin met het toevoegen van je eerste recept!</p>
          <Button asChild>
            <Link to="/recepten/nieuw">
              <PlusCircle className="h-4 w-4 mr-2" />
              Eerste Recept Toevoegen
            </Link>
          </Button>
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="text-center py-12">
          <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">Geen recepten gevonden</h3>
          <p className="text-gray-600 dark:text-slate-300 mb-6">Probeer andere zoektermen of filters</p>
          <Button variant="outline" onClick={clearFilters}>
            Wis Alle Filters
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecipes.map((recipe) => (
            <Card key={recipe.id} className="transition-shadow hover:shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600">
              <CardHeader>
                <CardTitle className="line-clamp-2 text-lg text-gray-900 dark:text-slate-50">{recipe.title}</CardTitle>
              </CardHeader>
              <CardContent>
                    <p className="mb-4 line-clamp-2 text-sm text-gray-700 dark:text-slate-200">{recipe.description}</p>
                
                <div className="mb-4 flex items-center gap-4 text-sm text-gray-600 dark:text-slate-200">
                  {(recipe.prep_time || recipe.cook_time) && (
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {(recipe.prep_time || 0) + (recipe.cook_time || 0)} min
                    </div>
                  )}
                  {recipe.servings && (
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {recipe.servings}
                    </div>
                  )}
                  <DifficultyIndicator difficulty={recipe.difficulty} showLabel={false} />
                </div>

                {recipe.tags && recipe.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {recipe.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="inline-block rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 dark:bg-orange-950 dark:text-orange-100"
                      >
                        {tag}
                      </span>
                    ))}
                    {recipe.tags.length > 3 && (
                      <span className="inline-block px-2 py-1 text-xs font-medium text-gray-600 dark:text-slate-100">
                        +{recipe.tags.length - 3} meer
                      </span>
                    )}
                  </div>
                )}

                <Button size="sm" asChild className="w-full">
                  <Link to={`/recepten/${recipe.id}`}>
                    Bekijk Recept
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
